import { useEffect, useMemo, useState } from "react";
import { Compass, Loader2, X } from "lucide-react";
import { fetchPlaceTrip, fetchPlaces } from "../lib/api";
import { dictionaries } from "../lib/i18n";
import type { CorridorOption, ExplorePlace, PlaceTrip, UiLang } from "../lib/types";
import { videoSearchUrl } from "../utils/videoLink";
import { FoldPanel } from "./FoldPanel";

interface Props {
  lang: UiLang;
  corridors: CorridorOption[];
  /** Åpner hele ruta stedet ligger på. */
  onOpenRoute: (corridorId: string) => void;
  /**
   * Lager en plan om nettopp dette stedet, og fører blikket dit.
   *
   * Meldt fra bruk: «velger jeg å dra til Trolltunga, hvorfor lander jeg her?»
   * — med et skjermbilde av skjemaet for Stavanger–Bergen. Og det var den
   * eneste veien ut av panelet: `onOpenRoute` åpner *ruta stedet ligger på*,
   * som er et helt annet spørsmål enn det som ble stilt.
   */
  onPlanTrip: (stopId: string, days: number, corridorId: string) => void;
}

/**
 * «Hva om jeg ikke vet om et sted?»
 *
 * ── Hvorfor dette finnes ───────────────────────────────────────────────────
 *
 * Fordi hvert eneste inngangspunkt appen hadde forutsatte at man visste hvor
 * man skulle. Rutevelgeren spør «hvilken av disse ti rutene?», og «vil du bare
 * til ett sted?» spør «hvilket sted?». Begge er gode spørsmål å få *hvis* man
 * har et svar. Den som bare vet at de vil til Norge hadde ingen vei inn.
 *
 * Spurt fra bruk med de ordene: «hva om jeg ikke vet om et sted — kan vi ha en
 * utforsk-seksjon med bildegalleri, hva stedet er, hva det er kjent for, og
 * hvilken rute det ligger på?».
 *
 * ── Hvorfor det utvider seg i flaten framfor å åpne et vindu ───────────────
 *
 * Et modalvindu må låse rullingen bak seg, fange tastaturfokus og få en egen
 * lukkeknapp som virker med Escape — og på telefon dekker det hele skjermen,
 * så man mister stedet man kom fra. Her vokser flisa i stedet ut til full
 * bredde der den står, og mosaikken flytter seg rundt den. Man ser fortsatt
 * hvor i lista man er, og «tilbake» er å trykke en gang til.
 *
 * ── Hva bildet er, og hva det ikke er ──────────────────────────────────────
 *
 * Ett foto per sted, i den store utgaven på 1280 px — `wide` i data/images.ts.
 * Det er *ikke* et galleri per sted: databasen har ett bilde hvert, hentet fra
 * Wikimedia med kreditering. Et ekte galleri ville krevd flere hundre nye
 * filer i repoet, og det er en avgjørelse om diskplass og opphavsrett som
 * hører hjemme et annet sted enn i en komponent.
 */
/**
 * Dagene en plan om ett sted starter på, når ingen har sagt noe annet.
 *
 * Samme tall som «ett sted»-panelet står på. Stedets eget minstekrav vinner
 * over det — se `minDays` — så dette er et *utgangspunkt*, ikke et tak.
 */
const DEFAULT_PLACE_DAYS = 3;

export function ExplorePanel({ lang, corridors, onOpenRoute, onPlanTrip }: Props) {
  const t = dictionaries[lang];
  const [places, setPlaces] = useState<ExplorePlace[]>([]);
  const [ready, setReady] = useState(false);
  const [query, setQuery] = useState("");
  const [route, setRoute] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PlaceTrip | null>(null);

  useEffect(() => {
    let live = true;
    fetchPlaces(lang)
      .then((result) => live && setPlaces(result.places ?? []))
      .catch(() => undefined)
      .finally(() => live && setReady(true));
    return () => {
      live = false;
    };
  }, [lang]);

  /*
   * Detaljene hentes først når et sted åpnes.
   *
   * Bakgrunnsteksten og attraksjonene for alle 118 stedene er godt over hundre
   * kilobyte, og de aller fleste av dem blir aldri lest. `/api/place-trip`
   * svarer alt med nettopp dette for ett sted, så det er den som spørres.
   */
  useEffect(() => {
    if (!openId) {
      setDetail(null);
      return;
    }
    let live = true;
    setDetail(null);
    fetchPlaceTrip(openId, 2, "avslappet", lang)
      .then((result) => live && setDetail(result))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [openId, lang]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return places.filter((place) => {
      if (route && !place.corridors.some((c) => c.id === route)) return false;
      if (!needle) return true;
      return (
        place.name.toLowerCase().includes(needle) ||
        place.blurb.toLowerCase().includes(needle)
      );
    });
  }, [places, query, route]);

  if (ready && places.length === 0) return null;

  return (
    <FoldPanel
      id="explore"
      title={t.exploreTitle}
      icon={<Compass size={14} />}
      summary={t.exploreCount(places.length)}
    >
      <p className="card-sub">{t.exploreHint}</p>

      <div className="explore-filters">
        <input
          className="explore-search"
          type="search"
          value={query}
          placeholder={t.exploreSearch}
          aria-label={t.exploreSearch}
          onChange={(event) => setQuery(event.target.value)}
        />
        <select
          className="explore-route"
          value={route}
          aria-label={t.exploreAllRoutes}
          onChange={(event) => setRoute(event.target.value)}
        >
          <option value="">{t.exploreAllRoutes}</option>
          {corridors.map((corridor) => (
            <option key={corridor.id} value={corridor.id}>
              {corridor.name}
            </option>
          ))}
        </select>
      </div>

      {!ready && (
        <p className="note">
          <Loader2 size={13} className="spin" /> {t.finding}
        </p>
      )}

      {ready && shown.length === 0 && <p className="note">{t.exploreNoMatch}</p>}

      <div className="explore-grid">
        {shown.map((place) => {
          const open = openId === place.id;
          return (
            <article
              key={place.id}
              className={`explore-tile ${open ? "explore-tile--open" : ""}`}
            >
              {/*
                Hele flisa er knappen. Et bilde med en egen liten knapp over er
                et treffområde man bommer på med tommelen.
              */}
              <button
                type="button"
                className="explore-hit"
                aria-expanded={open}
                onClick={() => setOpenId(open ? null : place.id)}
              >
                {place.image && (
                  <img
                    className="explore-photo"
                    src={open ? (place.image.wide ?? place.image.src) : place.image.src}
                    alt={place.name}
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <span className="explore-caption">
                  <b>{place.name}</b>
                  {place.signature && <span className="explore-star" aria-hidden="true">★</span>}
                </span>
              </button>

              {open && (
                <div className="explore-detail">
                  <button
                    type="button"
                    className="explore-close"
                    onClick={() => setOpenId(null)}
                    aria-label={t.exploreClose}
                  >
                    <X size={16} />
                  </button>

                  <p className="explore-blurb">{place.blurb}</p>

                  {place.hike && (
                    <p className="note">{t.exploreHikeDay(place.dwellHours)}</p>
                  )}

                  {/* Kreditering er et vilkår i lisensen, ikke en fotnote. */}
                  {place.image && (
                    <p className="explore-credit">
                      {place.image.credit} · {place.image.license} ·{" "}
                      <a href={place.image.sourceUrl} target="_blank" rel="noreferrer">
                        Wikimedia Commons
                      </a>
                    </p>
                  )}

                  {detail && detail.stop.id === place.id && detail.stop.offers && detail.stop.offers.length > 0 && (
                    <>
                      <h4 className="explore-heading">{t.exploreFamousFor}</h4>
                      <ul className="explore-offers">
                        {detail.stop.offers.map((offer) => (
                          <li key={offer.name}>
                            <b>{offer.name}</b>{" "}
                            <span className="explore-kind">{t.offerKind[offer.kind]}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {detail && detail.stop.id === place.id && detail.stop.history && (
                    <>
                      <h4 className="explore-heading">{t.exploreBackground}</h4>
                      <p className="explore-history">{detail.stop.history}</p>
                    </>
                  )}

                  {/*
                    Veien ut av panelet som svarer på spørsmålet som ble stilt.

                    Står over rutelenkene, ikke under: den som leser om
                    Trolltunga vil som regel til Trolltunga, ikke til
                    Stavanger–Bergen. Rutene er fortsatt der, ett hakk lenger
                    ned, for den som fikk lyst på mer.

                    Venter på `detail`, fordi `minDays` er derfra: en dagstur
                    til Trolltunga finnes ikke, og å be om tre dager for et sted
                    som trenger fire ville gitt en plan serveren måtte rette opp
                    bak brukerens rygg.
                  */}
                  {detail && detail.stop.id === place.id && (
                    <button
                      type="button"
                      className="place-plan"
                      onClick={() =>
                        onPlanTrip(
                          place.id,
                          Math.max(DEFAULT_PLACE_DAYS, detail.minDays),
                          detail.corridorId,
                        )
                      }
                    >
                      {t.placeTripPlan}
                    </button>
                  )}

                  <h4 className="explore-heading">{t.exploreOnRoute}</h4>
                  <p className="explore-routes">
                    {place.corridors.map((corridor) => (
                      <button
                        key={corridor.id}
                        type="button"
                        className="linkish"
                        onClick={() => onOpenRoute(corridor.id)}
                      >
                        {corridor.name}
                      </button>
                    ))}
                  </p>

                  <p className="explore-links">
                    {/*
                      Et *søk* på YouTube, ikke en påstått video: vi vet ikke
                      hvilken film som er den beste om Reine i dag, og en lenke
                      til en bestemt video råtner. Se utils/videoLink.ts.
                    */}
                    <a
                      href={videoSearchUrl(place.name, "Norway")}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t.exploreWatch}
                    </a>
                    <a href={`/place/${place.id}${lang === "no" ? "?lang=no" : ""}`}>
                      {t.exploreOpenPage}
                    </a>
                  </p>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </FoldPanel>
  );
}
